/**
 * AskPage — iter80: Ask TourismIntel (RAG Q&A with citations, PRO).
 * Route: /ask (auth required). Free tier: 3 questions/month (with upsell),
 * PRO: 100/month. Answers cite archive articles as [n] markers.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircleQuestion, Loader2, Sparkles, ExternalLink, History, Send, CornerDownRight } from "lucide-react";
import { API } from "../config";
import { useAuth, getCsrfValue } from "../AuthContext";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { useBillingEnabled } from "../components/ProUpsellCard";
import AskUpsellDark from "../components/AskUpsellDark";

const COVERAGE_STYLE = {
  strong: { label: "Strong coverage", bg: "#e7f6ec", fg: "#166534" },
  partial: { label: "Partial coverage", bg: "#fef6e0", fg: "#92400e" },
  insufficient: { label: "Insufficient coverage", bg: "#f1f3f4", fg: "#5f6b6d" },
};

const DAY_OPTIONS = [30, 90, 180, 365];

/* Minimal markdown → React (bold, lists, headings, [n] citation links).
   Renders text nodes only — no HTML injection from LLM output. */
function renderInline(text, keyPrefix, anchor) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[\d{1,2}\])/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={`${keyPrefix}-${i}`}>{p.slice(2, -2)}</strong>;
    const cite = p.match(/^\[(\d{1,2})\]$/);
    if (cite) {
      return (
        <sup key={`${keyPrefix}-${i}`}>
          <a href={`#ask-src-${anchor}-${cite[1]}`} data-testid={`ask-citation-${anchor}-${cite[1]}`}
            style={{ color: "#006874", fontWeight: 600, textDecoration: "none" }}>[{cite[1]}]</a>
        </sup>
      );
    }
    return p;
  });
}

function AnswerMarkdown({ md, anchor = "t0" }) {
  const blocks = (md || "").split(/\n{2,}/);
  return (
    <div className="space-y-3">
      {blocks.map((block, bi) => {
        const lines = block.split("\n").filter(l => l.trim());
        if (lines.every(l => /^\s*[-*•]\s+/.test(l))) {
          return (
            <ul key={bi} className="list-disc pl-5 space-y-1">
              {lines.map((l, li) => (
                <li key={li} className="text-sm leading-relaxed text-[#3b494c]">
                  {renderInline(l.replace(/^\s*[-*•]\s+/, ""), `${bi}-${li}`, anchor)}
                </li>
              ))}
            </ul>
          );
        }
        const h = block.match(/^(#{1,4})\s+(.*)$/);
        if (h) {
          return <p key={bi} className="text-sm font-bold text-[#191c1d]">{renderInline(h[2], `h-${bi}`, anchor)}</p>;
        }
        return <p key={bi} className="text-sm leading-relaxed text-[#3b494c]">{renderInline(block.replace(/\n/g, " "), `p-${bi}`, anchor)}</p>;
      })}
    </div>
  );
}

export default function AskPage() {
  const { user } = useAuth() || {};
  const [question, setQuestion] = useState(() => {
    // Deep-link prefill from other features (e.g. Insights "Ask about this
    // destination"): /ask?q=<question>
    try {
      return new URLSearchParams(window.location.search).get("q") || "";
    } catch {
      return "";
    }
  });
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState([]);         // conversation turns [{id, question, answer_markdown, citations, coverage}]
  const [followUp, setFollowUp] = useState("");     // follow-up input (iter137)
  const lastReqRef = useRef(null);                  // {q, parentId} for the retry button
  const [streamText, setStreamText] = useState(""); // live SSE text while the LLM writes
  const [error, setError] = useState(null);         // {message, upsell}
  const [history, setHistory] = useState([]);
  const [quota, setQuota] = useState(null);
  const billingEnabled = useBillingEnabled();

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ask/history`);
      setHistory(r.data.items || []);
      setQuota(r.data.quota || null);
    } catch { /* best-effort */ }
  }, []);

  useEffect(() => { document.title = "Ask TourismIntel — AI answers from the archive"; loadHistory(); }, [loadHistory]);

  const applyAskError = (status, detail) => {
    if (status === 429 && detail?.error === "ask_quota_exceeded") {
      setError({ message: detail.upsell?.message || "Monthly question limit reached.", upsell: !!detail.upsell });
      setQuota(q2 => ({ ...(q2 || {}), used: detail.used, limit: detail.limit, tier: detail.tier, remaining: 0 }));
    } else if (status === 429) {
      setError({ message: detail?.message || "Ask has reached today's capacity — try again tomorrow." });
    } else if (!status || status >= 500) {
      // Infra/gateway errors (Cloudflare 520/524, network) return HTML, not JSON
      setError({ message: "The server took too long or is briefly unavailable — please retry.", retry: true });
    } else {
      setError({ message: typeof detail === "string" ? detail : "Something went wrong — please retry." });
    }
  };

  // Classic non-streaming fallback (used when the SSE transport is unavailable)
  const submitClassic = async (q, parentId) => {
    try {
      const r = await axios.post(`${API}/ask`, { question: q, days, parent_id: parentId || null });
      setThread(t => (parentId ? [...t, { ...r.data, question: q }] : [{ ...r.data, question: q }]));
      setFollowUp("");
      if (r.data.quota) setQuota(r.data.quota);
      loadHistory();
    } catch (err) {
      applyAskError(err?.response?.status, err?.response?.data?.detail);
    }
  };

  const doSubmit = async ({ q, parentId }) => {
    if (loading) return;
    setLoading(true); setError(null); setStreamText("");
    if (!parentId) setThread([]);
    let receivedAny = false;
    try {
      const res = await fetch(`${API}/ask/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfValue() },
        body: JSON.stringify({ question: q, days, parent_id: parentId || null }),
      });
      if (!res.ok) {
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* HTML error page */ }
        applyAskError(res.status, detail);
        return;
      }
      receivedAny = true;
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "", acc = "", finalData = null, errData = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const raw = buf.slice(0, idx); buf = buf.slice(idx + 2);
          const ev = raw.match(/^event: (.+)$/m)?.[1];
          const dataStr = raw.match(/^data: (.+)$/m)?.[1];
          if (!dataStr) continue;
          let data; try { data = JSON.parse(dataStr); } catch { continue; }
          if (ev === "delta") {
            acc += data.text || "";
            setStreamText(acc.replace(/^\s*COVERAGE:\s*\w+\s*/i, "").replace(/\n\s*FOLLOW[- ]?UPS?:[\s\S]*$/i, ""));
          } else if (ev === "final") finalData = data;
          else if (ev === "error") errData = data;
        }
      }
      if (finalData) {
        setThread(t => (parentId ? [...t, { ...finalData, question: q }] : [{ ...finalData, question: q }]));
        setFollowUp("");
        if (finalData.quota) setQuota(finalData.quota);
        loadHistory();
      } else {
        applyAskError(errData?.status || 503, errData?.message || null);
      }
    } catch {
      if (!receivedAny) {
        // SSE transport blocked/unsupported → classic endpoint (no double charge)
        await submitClassic(q, parentId);
      } else {
        setError({ message: "The connection dropped mid-answer — please retry.", retry: true });
      }
    } finally {
      setLoading(false); setStreamText("");
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    const q = question.trim();
    if (q.length < 5 || loading) return;
    lastReqRef.current = { q, parentId: null };
    doSubmit(lastReqRef.current);
  };

  const submitFollowUp = (e) => {
    e?.preventDefault();
    const q = followUp.trim();
    const last = thread[thread.length - 1];
    if (q.length < 5 || loading || !last?.id) return;
    lastReqRef.current = { q, parentId: last.id };
    doSubmit(lastReqRef.current);
  };

  // Suggested follow-up chip (iter141): tap-and-go, keeps the thread context
  const submitSuggested = (q) => {
    const last = thread[thread.length - 1];
    if (loading || !last?.id) return;
    lastReqRef.current = { q, parentId: last.id };
    doSubmit(lastReqRef.current);
  };

  const retryLast = () => { if (lastReqRef.current && !loading) doSubmit(lastReqRef.current); };

  const isFree = quota?.tier === "free";
  const hasLimit = quota?.limit != null;
  const quotaExhausted = hasLimit && quota.remaining === 0;
  const lastQuestion = hasLimit && quota.remaining === 1;
  const resetLabel = (() => {
    const n = new Date();
    return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth() + 1, 1))
      .toLocaleDateString("en", { month: "short", day: "numeric", timeZone: "UTC" });
  })();

  // Pricing instrumentation: how many users actually hit the quota wall
  const quotaZeroFired = useRef(false);
  useEffect(() => {
    if (!quotaExhausted || quotaZeroFired.current) return;
    quotaZeroFired.current = true;
    try { window.posthog?.capture("ask_quota_exhausted", { tier: quota?.tier, limit: quota?.limit }); } catch { /* best-effort */ }
  }, [quotaExhausted, quota]);

  return (
    <div data-testid="ask-page" style={{ background: "#fafbfb", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <PublicPageNav active="ask" />

      <main className="flex-1 w-full" style={{ maxWidth: 1024, margin: "0 auto", padding: "40px 24px 64px" }}>
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircleQuestion className="w-5 h-5 text-[#006874]" />
            <span className="text-xs font-bold tracking-widest uppercase text-[#006874]">Ask TourismIntel</span>
            <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-[#006874]/10 text-[#006874]">AI</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#191c1d]" style={{ letterSpacing: "-0.02em" }}>
            Ask anything. Answered from the archive, with sources.
          </h1>
          <p className="text-sm text-[#6b7a7d] mt-2 max-w-xl">
            Questions are answered exclusively from TourismIntel&apos;s news archive — every claim cites the article it comes from.
          </p>
        </header>

        <form onSubmit={submit} className="bg-white rounded-2xl p-5" style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)" }}>
          <textarea
            data-testid="ask-input"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e); }}
            placeholder="e.g. How are European hotels adopting agentic AI for direct bookings?"
            rows={3}
            maxLength={500}
            className="w-full text-sm text-[#191c1d] placeholder-[#9aa7a9] outline-none resize-none"
            style={{ background: "transparent" }}
          />
          <div className="flex justify-end">
            <span data-testid="ask-char-counter" role="status" aria-live="polite" className="text-[11px] tabular-nums"
              style={{ color: question.length >= 480 ? "#b45309" : "#9aa7a9" }}>
              {question.length}/500
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3" style={{ borderTop: "1px solid #eef1f2" }}>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#6b7a7d]">Time window:</span>
              {DAY_OPTIONS.map(d => (
                <button key={d} type="button" data-testid={`ask-days-${d}`}
                  onClick={() => setDays(d)}
                  className="text-xs px-2.5 py-1 rounded-full font-medium transition-colors"
                  style={days === d ? { background: "#006874", color: "#fff" } : { background: "#f1f4f5", color: "#5f6b6d" }}>
                  {d}d
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              {hasLimit && (
                <span data-testid="ask-quota" className="flex items-center gap-2 text-xs text-[#6b7a7d]">
                  {quota.limit <= 10 && (
                    <span data-testid="ask-quota-dots" className="flex items-center gap-1">
                      {[...Array(quota.limit)].map((_, i) => (
                        <span key={i} className="w-1.5 h-1.5 rounded-full"
                          style={{ background: i < quota.remaining ? "#006874" : "#d5dcdd" }} />
                      ))}
                    </span>
                  )}
                  <span>
                    {quota.remaining} of {quota.limit}{isFree ? " free" : ""} questions left
                    <span data-testid="ask-quota-reset" className="text-[#9aa7a9]"> · resets {resetLabel}</span>
                  </span>
                </span>
              )}
              {!hasLimit && quota?.tier === "reviewer" && (
                <span data-testid="ask-quota" className="text-xs text-[#6b7a7d]">Unlimited (reviewer)</span>
              )}
              <button
                type="submit"
                data-testid="ask-submit"
                disabled={loading || question.trim().length < 5 || quotaExhausted}
                className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white transition-opacity disabled:opacity-40"
                style={{ background: "#006874" }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Thinking…" : "Ask"}
              </button>
            </div>
          </div>
          {lastQuestion && (
            <p data-testid="ask-last-question-warning" className="mt-3 text-xs font-medium rounded-lg px-3 py-2"
              style={{ background: "#fdf6e8", color: "#8a5a06" }}>
              Heads up — this is your last{isFree ? " free" : ""} question until {resetLabel}.
            </p>
          )}
          {quotaExhausted && (
            <p data-testid="ask-zero-state-note" className="mt-3 text-xs rounded-lg px-3 py-2 bg-[#f1f4f5] text-[#5f6b6d]">
              You&apos;ve used this month&apos;s {quota.limit}{isFree ? " free" : ""} questions. Resets {resetLabel}
              {isFree && billingEnabled === true && (
                <> · <a href="/pro" data-testid="ask-zero-state-upgrade" className="font-semibold text-[#006874] hover:underline"
                  onClick={() => { try { window.posthog?.capture("paywall_clicked", { surface: "ask_zero_state" }); } catch { /* best-effort */ } }}>Upgrade for more</a></>
              )}. You can still type your question — asking unlocks on reset.
            </p>
          )}
        </form>

        {error && (
          <div data-testid="ask-error" className="mt-5 rounded-2xl p-5 bg-white" style={{ boxShadow: "0 0 0 1px rgba(180,83,9,0.25)" }}>
            <p className="text-sm text-[#92400e]">{error.message}</p>
            {error.retry && (
              <button type="button" data-testid="ask-error-retry-btn" onClick={retryLast}
                disabled={loading}
                className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "#006874" }}>
                Retry
              </button>
            )}
            {error.upsell && billingEnabled === true && (
              <a href="/pro" data-testid="ask-quota-upsell-cta"
                className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-full text-sm font-semibold text-white"
                style={{ background: "#006874" }}>
                <Sparkles className="w-3.5 h-3.5" /> Upgrade to PRO
              </a>
            )}
          </div>
        )}

        {thread.map((turn, ti) => (
          <section key={turn.id || ti} data-testid={ti === 0 ? "ask-answer" : `ask-answer-${ti}`}
            className="mt-5 bg-white rounded-2xl p-6" style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)" }}>
            {ti > 0 && (
              <p data-testid={`ask-turn-question-${ti}`} className="flex items-start gap-1.5 text-sm font-semibold text-[#191c1d] mb-3">
                <CornerDownRight className="w-4 h-4 mt-0.5 text-[#9aa7a9] shrink-0" />
                {turn.question}
              </p>
            )}
            <div className="flex items-center justify-between mb-4">
              <span data-testid={ti === 0 ? "ask-coverage" : `ask-coverage-${ti}`} className="text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full"
                style={{ background: (COVERAGE_STYLE[turn.coverage] || COVERAGE_STYLE.partial).bg, color: (COVERAGE_STYLE[turn.coverage] || COVERAGE_STYLE.partial).fg }}>
                {(COVERAGE_STYLE[turn.coverage] || COVERAGE_STYLE.partial).label}
              </span>
              {turn.latency_ms != null && <span className="text-[11px] text-[#9aa7a9]">{(turn.latency_ms / 1000).toFixed(1)}s</span>}
            </div>
            <AnswerMarkdown md={turn.answer_markdown} anchor={`t${ti}`} />
            {turn.citations?.length > 0 && (
              <div data-testid={ti === 0 ? "ask-sources" : `ask-sources-${ti}`} className="mt-5 pt-4" style={{ borderTop: "1px solid #eef1f2" }}>
                <p className="text-xs font-bold tracking-wider uppercase text-[#6b7a7d] mb-2">Sources</p>
                <ol className="space-y-1.5">
                  {turn.citations.map(c => (
                    <li key={c.n} id={`ask-src-t${ti}-${c.n}`} data-testid={`ask-source-t${ti}-${c.n}`} className="text-sm flex items-baseline gap-2">
                      <span className="font-semibold text-[#006874] shrink-0">[{c.n}]</span>
                      <span className="text-[#3b494c]">
                        {c.url ? (
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {c.title} <ExternalLink className="inline w-3 h-3 text-[#9aa7a9]" />
                          </a>
                        ) : c.title}
                        <span className="text-[#9aa7a9]"> — {c.source}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </section>
        ))}

        {loading && streamText && (
          <section data-testid="ask-streaming" className="mt-5 bg-white rounded-2xl p-6" style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#006874]" />
              <span className="text-[10px] font-bold tracking-wider uppercase text-[#6b7a7d]">Answering…</span>
            </div>
            <AnswerMarkdown md={streamText} anchor="live" />
          </section>
        )}

        {!loading && thread.length > 0 && thread[thread.length - 1]?.id && (
          <>
            {Array.isArray(thread[thread.length - 1].suggested_followups)
              && thread[thread.length - 1].suggested_followups.length > 0
              && !quotaExhausted && (
              <div data-testid="ask-suggested-followups" className="mt-3 flex flex-wrap gap-2">
                {thread[thread.length - 1].suggested_followups.map((q, i) => (
                  <button key={i} type="button" data-testid={`ask-suggested-followup-${i}`}
                    onClick={() => submitSuggested(q)}
                    className="text-left text-xs px-3.5 py-2 rounded-full transition-colors bg-white text-[#3b494c] hover:bg-[#eef4f5]"
                    style={{ boxShadow: "0 0 0 1px rgba(0,104,116,0.25)" }}>
                    {q}
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={submitFollowUp} data-testid="ask-followup-form"
            className="mt-3 flex items-center gap-2 bg-white rounded-2xl px-4 py-3"
            style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.06)" }}>
            <CornerDownRight className="w-4 h-4 text-[#9aa7a9] shrink-0" />
            <input data-testid="ask-followup-input" value={followUp}
              onChange={e => setFollowUp(e.target.value)}
              placeholder="Ask a follow-up — this answer stays in context…" maxLength={500}
              className="flex-1 text-sm outline-none placeholder-[#9aa7a9] text-[#191c1d]"
              style={{ background: "transparent" }} />
            <button type="submit" data-testid="ask-followup-submit"
              disabled={followUp.trim().length < 5 || quotaExhausted}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: "#006874" }}>
              <Send className="w-3 h-3" /> Follow-up
            </button>
          </form>
          </>
        )}

        {isFree && (
          <div className="mt-5">
            <AskUpsellDark proLimit={100} />
          </div>
        )}

        {history.length > 0 && (
          <section data-testid="ask-history" className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-[#6b7a7d]" />
              <h2 className="text-sm font-bold text-[#191c1d]">Your recent questions</h2>
            </div>
            <div className="space-y-2">
              {history.map((h, i) => (
                <button key={h.id} data-testid={`ask-history-item-${i}`}
                  onClick={() => { setThread([h]); setError(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="w-full text-left bg-white rounded-xl px-4 py-3 transition-shadow hover:shadow-sm"
                  style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.05)" }}>
                  <p className="text-sm text-[#191c1d] font-medium">{h.parent_id ? "↳ " : ""}{h.question}</p>
                  <p className="text-[11px] text-[#9aa7a9] mt-0.5">
                    {(COVERAGE_STYLE[h.coverage] || COVERAGE_STYLE.partial).label}
                    {h.created_at ? ` · ${new Date(h.created_at).toLocaleDateString()}` : ""}
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>

      <PublicPageFooter />
    </div>
  );
}
